import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import { CONTENT_WIDTH, PAGE_STACK, TEXT, SURFACE, SURFACE_RAISED, TAG_ACCENT } from '../../uiTokens';

function mailto(subject, intro) {
  const body = `${intro}%0D%0A%0D%0APlease provide more information about pricing, features, and setup.%0D%0A%0D%0ABest regards`;
  return `mailto:info@nvteh.com?subject=${encodeURIComponent(subject)}&body=${body}`;
}

const MAILTO_HOSTED_TEAM = mailto(
  'Hosted Team Plan',
  "Hi,%0D%0A%0D%0AI'm interested in the Hosted Team plan for hushify.io.",
);
const MAILTO_SELF_HOSTED_TEAM = mailto(
  'Self-Hosted Team License',
  "Hi,%0D%0A%0D%0AI'm interested in a Self-Hosted Team license for hushify.io.",
);
const MAILTO_SELF_HOSTED_BUSINESS = mailto(
  'Self-Hosted Business License',
  "Hi,%0D%0A%0D%0AI'm interested in a Self-Hosted Business license for hushify.io.",
);
const MAILTO_ENTERPRISE = mailto(
  'Enterprise Plan',
  "Hi,%0D%0A%0D%0AI'm interested in an Enterprise plan for hushify.io.",
);

const HOSTED_PLANS = [
  {
    id: 'free',
    title: 'Free',
    price: '€0',
    period: 'forever',
    subtitle: 'Hosted at hushify.io',
    features: [
      'Unlimited secret sharing',
      'One-time links with browser encryption',
      'Custom link expiration',
      'QR codes and multiple links per secret',
      'Email when a link is opened',
      'IP restrictions',
    ],
    ctaVariant: 'secondary',
    ctaLabel: 'Current plan',
    ctaDisabled: true,
  },
  {
    id: 'team',
    title: 'Team',
    priceMonthly: '€29',
    priceYearly: '€290',
    periodMonthly: 'per month',
    periodYearly: 'per year (€24/mo)',
    subtitle: 'For teams on the hosted service',
    highlight: 'Most popular',
    features: [
      'Everything in Free',
      'Up to 25 members',
      'Slack and Discord workspace integration',
      'Private team workspace',
      'Activity log (metadata only)',
      'Longer expiration options',
      'Custom link domain (on request)',
      'Priority email support',
    ],
    ctaVariant: 'primary',
    ctaLabel: 'Get Team',
    ctaHref: MAILTO_HOSTED_TEAM,
  },
  {
    id: 'enterprise',
    title: 'Enterprise',
    price: 'Custom',
    period: 'contact us',
    subtitle: 'Dedicated hosted deployment',
    features: [
      'Everything in Team',
      'Dedicated EU instance',
      'Unlimited members',
      'SAML/SSO (on request)',
      'DPA and SLA options',
      'Dedicated support',
    ],
    ctaVariant: 'outline',
    ctaLabel: 'Contact sales',
    ctaHref: MAILTO_ENTERPRISE,
  },
];

const SELF_HOSTED_PLANS = [
  {
    id: 'team',
    title: 'Team',
    price: '€490',
    period: 'per year, per instance',
    subtitle: 'Commercial license for small teams',
    highlight: 'Most popular',
    features: [
      'Self-hosted deployment on your infrastructure',
      'Up to 50 users',
      'Commercial license',
      'Slack and Discord integration setup',
      'Metadata analytics dashboard',
      'Email notifications',
      'Software updates',
      'Email support (2 business days)',
    ],
    ctaVariant: 'primary',
    ctaLabel: 'Get Team license',
    ctaHref: MAILTO_SELF_HOSTED_TEAM,
  },
  {
    id: 'business',
    title: 'Business',
    price: '€990',
    period: 'per year, per instance',
    subtitle: 'For larger organizations',
    features: [
      'Everything in Team',
      'Up to 250 users',
      'Audit logging module',
      'Custom branding',
      'SSO/OIDC (on request)',
      'One guided installation session',
      'Priority support',
    ],
    ctaVariant: 'primary',
    ctaLabel: 'Get Business license',
    ctaHref: MAILTO_SELF_HOSTED_BUSINESS,
  },
  {
    id: 'enterprise',
    title: 'Enterprise',
    price: 'Custom',
    period: 'contact us',
    subtitle: 'Advanced deployments and modules',
    features: [
      'Everything in Business',
      'Unlimited users',
      'Multi-instance deployments',
      'Team vaults, RBAC, geofencing (on request)',
      'PKI and CI/CD modules (on request)',
      'SLA and dedicated support',
      'DPA',
    ],
    note: 'Audit logging, file encryption, PKI, and CI/CD integrations are available as modules. Contact us to confirm what fits your deployment.',
    ctaVariant: 'outline',
    ctaLabel: 'Contact sales',
    ctaHref: MAILTO_ENTERPRISE,
  },
];

const FAQ = [
  {
    q: 'What is included in Free?',
    a: 'Browser encryption, one-time links, custom expiration, QR codes, email when a link is opened, and IP restrictions on the hosted service.',
  },
  {
    q: 'Is the open-source core still available?',
    a: 'Yes. The MIT-licensed core remains open source, but self-hosted commercial plans are required for production deployments with support, integrations, and optional modules.',
  },
  {
    q: 'What counts as a user or instance?',
    a: 'On hosted Team, a member is anyone in your workspace who creates or manages secrets. On self-hosted plans, a user is anyone who can create secrets on your instance. One license covers one domain or deployment.',
  },
  {
    q: 'Which features need a license?',
    a: 'Slack and Discord setup, analytics dashboard, audit logging, branding, SSO, and priority support require a paid self-hosted or hosted Team license. Enterprise-only items stay labelled on request until confirmed for your deployment.',
  },
  {
    q: 'How does self-hosting work?',
    a: 'You deploy the Hushify stack (UI and API) on your own infrastructure using our Docker or Kubernetes assets.',
  },
  {
    q: 'Which Enterprise features are live?',
    a: 'SSO, team vaults, geofencing, and RBAC vary by deployment. Contact us to confirm what is available for your environment.',
  },
  {
    q: 'Can I upgrade later?',
    a: 'Yes. Move from hosted Free to Team, or upgrade between self-hosted license tiers, at any time.',
  },
];

function SegmentedToggle({ options, value, onChange, ariaLabel }) {
  return (
    <div
      className="inline-flex rounded-lg border border-line bg-surface p-1 gap-1"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map(({ id, label }) => {
        const isActive = value === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={`min-h-9 px-3.5 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ${
              isActive
                ? 'bg-accent text-accent-fg shadow-e1'
                : 'text-muted hover:text-fg hover:bg-surface-2'
            }`}
            aria-pressed={isActive}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function PlanCard({ plan, billingPeriod }) {
  const isHighlighted = Boolean(plan.highlight);
  const price = plan.price
    ?? (billingPeriod === 'yearly' ? plan.priceYearly : plan.priceMonthly);
  const period = plan.period
    ?? (billingPeriod === 'yearly' ? plan.periodYearly : plan.periodMonthly);

  const cta = plan.ctaDisabled ? (
    <Button variant={plan.ctaVariant} block disabled>{plan.ctaLabel}</Button>
  ) : (
    <Button as="a" href={plan.ctaHref} variant={plan.ctaVariant} block>{plan.ctaLabel}</Button>
  );

  return (
    <article className={`${isHighlighted ? SURFACE_RAISED : SURFACE} p-6 flex flex-col relative`}>
      {isHighlighted && (
        <span className={`${TAG_ACCENT} absolute top-5 right-5`}>{plan.highlight}</span>
      )}
      <div>
        <h2 className={TEXT.h3}>{plan.title}</h2>
        <p className={`${TEXT.caption} mt-0.5`}>{plan.subtitle}</p>
      </div>
      <div className="mt-5">
        <p className="text-[32px] leading-none font-semibold tracking-[-0.02em] text-fg tabular-nums">{price}</p>
        {period && <p className={`${TEXT.caption} mt-1.5`}>{period}</p>}
      </div>

      <ul className="mt-6 flex flex-col gap-2.5 border-t border-line pt-5">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5 text-sm text-muted">
            <Check className="h-4 w-4 shrink-0 mt-0.5 text-accent" strokeWidth={2.5} aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {plan.note && <p className={`${TEXT.caption} mt-5 leading-relaxed`}>{plan.note}</p>}

      <div className="mt-auto pt-7">{cta}</div>
    </article>
  );
}

export default function PricingPage({ initialTab = 'hosted' }) {
  const [deploymentTab, setDeploymentTab] = useState(initialTab === 'self-hosted' ? 'self-hosted' : 'hosted');
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    setDeploymentTab(initialTab === 'self-hosted' ? 'self-hosted' : 'hosted');
  }, [initialTab]);

  const plans = deploymentTab === 'hosted' ? HOSTED_PLANS : SELF_HOSTED_PLANS;
  const gridClass = 'grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch';

  return (
    <div className={`${CONTENT_WIDTH} ${PAGE_STACK}`}>
      <PageHeader
        eyebrow="Pricing"
        title="Start free. Run it yourself when you need to."
        description="The hosted service is free for everyone. Paid plans add team controls, integrations, and support."
      />

      <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <SegmentedToggle
          ariaLabel="Deployment type"
          value={deploymentTab}
          onChange={setDeploymentTab}
          options={[
            { id: 'hosted', label: 'Hosted' },
            { id: 'self-hosted', label: 'Self-hosted' },
          ]}
        />

        {deploymentTab === 'hosted' && (
          <SegmentedToggle
            ariaLabel="Billing period"
            value={billingPeriod}
            onChange={setBillingPeriod}
            options={[
              { id: 'monthly', label: 'Monthly' },
              { id: 'yearly', label: 'Yearly' },
            ]}
          />
        )}
      </div>

      <div className={`mt-8 sm:mt-10 ${gridClass}`}>
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} billingPeriod={billingPeriod} />
        ))}
      </div>

      <p className={`${TEXT.caption} mt-6 text-center`}>
        Similar tools typically charge €35-125/mo hosted and €149-1,400/yr self-hosted.
      </p>

      <section className="mt-16 sm:mt-24" aria-labelledby="faq">
        <h2 id="faq" className={`${TEXT.h2} mb-6`}>Questions</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-12 border-t border-line">
          {FAQ.map(({ q, a }) => (
            <div key={q} className="py-6 border-b border-line">
              <dt className={TEXT.h3}>{q}</dt>
              <dd className={`${TEXT.body} mt-2`}>{a}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}
